<!-- Компонент ничего не показывает, но шаблон не может быть пустым: на сервере
     пустой шаблон не даёт ни одного узла, а на клиенте разворачивается в пустой
     фрагмент — число детей у родителя расходится и гидратация ломается.
     Один скрытый узел отдаётся одинаково с обеих сторон. -->
<template><span hidden aria-hidden="true"></span></template>

<script setup>
import { defineProps, onMounted, onUnmounted, ref } from 'vue'

const props = defineProps({
  backendurl: String,
  tracking: {
    type: Object,
    default: () => ({})
  }
})

const hasSent = ref(false)
const ymUid = ref(null)
const isMobile = ref(false)

const ENGAGE_TIMEOUT_MS = 15000  // 15 секунд на странице
const ENGAGE_SCROLL_PCT = 30     // 30% прокрутки страницы

onMounted(() => {
  if (typeof window === 'undefined') return

  isMobile.value = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)

  if (import.meta.env.DEV) {
    console.log('🎯 Tracking mounted:', { url: props.backendurl })
  }

  let timerDone = false
  let scrollDone = false

  function tryEngage() {
    // Вовлечённость = провёл на странице ≥15с (скролл больше не обязателен)
    if (hasSent.value || !timerDone) return
    if (import.meta.env.DEV) console.log('✅ Engaged: timer (15s)')
    sendToServer()
  }

  // Условие 1: таймер 15 секунд
  const timer = setTimeout(() => {
    timerDone = true
    if (import.meta.env.DEV) console.log('⏱ Timer done (15s)')
    tryEngage()
  }, ENGAGE_TIMEOUT_MS)

  // Условие 2: прокрутка 30% страницы
  function onScroll() {
    if (scrollDone || hasSent.value) return
    const scrolled = window.scrollY || window.pageYOffset
    const docHeight = document.documentElement.scrollHeight - window.innerHeight
    if (docHeight <= 0) return
    if ((scrolled / docHeight) * 100 >= ENGAGE_SCROLL_PCT) {
      scrollDone = true
      if (import.meta.env.DEV) console.log(`📜 Scroll done (≥${ENGAGE_SCROLL_PCT}%)`)
      tryEngage()
    }
  }

  window.addEventListener('scroll', onScroll, { passive: true })

  onUnmounted(() => {
    clearTimeout(timer)
    window.removeEventListener('scroll', onScroll)
  })
})

function sendToServer() {
  // Проверка на дублирование
  if (hasSent.value) {
    if (import.meta.env.DEV) console.log('🔄 Уже отправлено')
    return
  }

  // Проверка обязательных полей
  if (!props.tracking?.visit_uuid) {
    if (import.meta.env.DEV) console.log('❌ Нет visit_uuid')
    return
  }

  if (!props.backendurl) {
    if (import.meta.env.DEV) console.log('❌ Нет backendurl')
    return
  }

  hasSent.value = true

  // Формируем данные
  const formData = new FormData()
  formData.append('visit_uuid', props.tracking.visit_uuid)
  formData.append('url', props.backendurl)

  // CSRF токен
  const csrfToken = getCsrfToken()
  if (csrfToken) {
    formData.append('_token', csrfToken)
  }

  if (import.meta.env.DEV) {
    console.log('🚀 Отправка вовлечения:', props.backendurl)
  }

  // Отправляем
  fetch('/events', {
    method: 'POST',
    body: formData,
  })
    .then(async response => {
      if (import.meta.env.DEV) {
        console.log(`📊 HTTP статус: ${response.status}`)
      }

      if (response.ok) {
        const data = await response.json()
        if (import.meta.env.DEV) {
          console.log('✅ Успешно сохранено:', data.message)
        }
      } else if (import.meta.env.DEV) {
        try {
          const error = await response.json()
          console.error('❌ Ошибка сервера:', error)
        } catch {
          console.error(`❌ HTTP ошибка ${response.status}`)
        }
      }
    })
    .catch(error => {
      if (import.meta.env.DEV) {
        console.error('❌ Ошибка сети:', error)
      }
    })
}

function getCsrfToken() {
  try {
    // 1. Из meta тега (стандартный способ Laravel)
    const meta = document.querySelector('meta[name="csrf-token"]')
    if (meta && meta.content) {
      return meta.content
    }

    // 2. Из input поля
    const input = document.querySelector('input[name="_token"]')
    if (input && input.value) {
      return input.value
    }

    // 3. Из кук (для инерции и SPA)
    const match = document.cookie.match(/XSRF-TOKEN=([^;]+)/)
    if (match) {
      return decodeURIComponent(match[1])
    }

    return ''
  } catch (e) {
    return ''
  }
}
</script>